<script lang="ts">
import { ref } from "vue"

const DISMISSED_KEY = "hub.incidents.dismissed"

function readDismissed(): Record<string, string> {
  try {
    return JSON.parse(localStorage.getItem(DISMISSED_KEY) ?? "{}") as Record<string, string>
  } catch {
    return {}
  }
}

/** Put away: the incident's id and the update it was put away at. One for every banner (Home's and Messages'). */
const dismissed = ref<Record<string, string>>(readDismissed())
</script>

<script setup lang="ts">
import { currentIncidents, useMessaging } from "../composables/useMessaging"
import { dateLocale, t } from "../lib/i18n"
import { severityLabel, statusLabel } from "../lib/incidents"

// Incidents in Johku at the top of a screen (the app's Home and Messages, the POS): what's wrong and
// how it's going, until a few hours after it's resolved. Tapping one opens it; the sheet opens here,
// also for the team's list and a new incident (openIncidentId "list", "new"). Each can be put away
// until its next update, on this device.
const props = withDefaults(defineProps<{ theme?: "app" | "pos" }>(), { theme: undefined })
const injected = inject<"app" | "pos">("hubTheme", "app")
provide("hubTheme", props.theme ?? injected)

const hub = useMessaging()

function dismiss(id: string, updatedAt: string) {
  // Only the incidents still shown are worth remembering
  const known = new Set(hub.incidents.map((i) => i.id))
  dismissed.value = Object.fromEntries([...Object.entries(dismissed.value).filter(([k]) => known.has(k)), [id, updatedAt]])
  try {
    localStorage.setItem(DISMISSED_KEY, JSON.stringify(dismissed.value))
  } catch {
    // Storage blocked: put away until the page reloads
  }
}

const shown = computed(() => (hub.status === "ready" ? currentIncidents().filter((i) => dismissed.value[i.id] !== i.updatedAt) : []))

function when(iso: string): string {
  const d = new Date(iso)
  return new Date().toDateString() === d.toDateString()
    ? d.toLocaleTimeString(dateLocale(), { hour: "2-digit", minute: "2-digit" })
    : d.toLocaleString(dateLocale(), { day: "numeric", month: "numeric", hour: "2-digit", minute: "2-digit" })
}
</script>

<template>
  <div v-if="shown.length" class="hub-incidents" :class="(props.theme ?? injected) === 'pos' ? 'hub-ui hub-theme-pos' : ''" role="status">
    <button
      v-for="incident in shown"
      :key="incident.id"
      type="button"
      class="incident"
      :class="incident.resolvedAt ? 'resolved' : incident.severity"
      @click="hub.openIncidentId = incident.id"
    >
      <span class="mark"><HubIcon :name="incident.resolvedAt ? 'check' : 'warning'" :size="22" filled /></span>
      <span class="text">
        <strong>{{ incident.title }}</strong>
        <span>
          {{ incident.resolvedAt ? statusLabel(t, "resolved") : `${severityLabel(t, incident.severity)} · ${statusLabel(t, incident.status)}` }}
          · {{ when(incident.updatedAt) }}
        </span>
      </span>
      <span
        class="dismiss"
        role="button"
        tabindex="0"
        :aria-label="t('Close')"
        @click.stop="dismiss(incident.id, incident.updatedAt)"
        @keydown.enter.stop.prevent="dismiss(incident.id, incident.updatedAt)"
      >
        <HubIcon name="close" :size="20" />
      </span>
    </button>
  </div>
  <HubIncidentSheet v-if="hub.openIncidentId" :key="hub.openIncidentId" @close="hub.openIncidentId = null" />
</template>

<style scoped>
.hub-incidents {
  display: grid;
  gap: 1px;
  background: transparent;
}

/* Room before what follows (Messages' chips) */
.hub-incidents:not(.hub-theme-pos) {
  margin-bottom: 10px;
}

/* The icon and text line up with the conversations' pictures and names below */
.incident {
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  min-height: 56px;
  padding: 10px max(12px, env(safe-area-inset-right)) 10px max(16px, env(safe-area-inset-left));
  border: 0;
  text-align: left;
  /* Johku's yellow for anything going on now: noticed at a glance, dark text in both themes */
  background: var(--hub-signal);
  color: var(--hub-on-signal);
}

.incident.outage {
  background: #b3261e;
  color: #ffffff;
}

.incident.resolved {
  background: var(--hub-good);
  /* White on the light themes' green, dark on the dark theme's */
  color: var(--hub-surface);
}

.mark {
  display: grid;
  place-items: center;
  width: 44px;
  flex: none;
}

.text {
  flex: 1;
  min-width: 0;
  display: grid;
}

.text strong {
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.text span {
  font-size: 0.85rem;
  opacity: 0.9;
}

.dismiss {
  display: grid;
  place-items: center;
  width: 36px;
  height: 36px;
  margin: -6px -4px -6px 0;
}

.hub-theme-pos .incident {
  gap: 12px;
  min-height: 44px;
  padding: 8px 15px;
}

.hub-theme-pos .mark {
  width: auto;
}
</style>
